function bootClinicAssistant() {
  const root = document.querySelector(".clinic-assistant");
  if (!root) return;

  const TEMPORARY_UNAVAILABLE_MESSAGE = "The portal guide is temporarily unavailable right now. Please try again shortly.";
  const pageContext = JSON.parse(root.dataset.pageContext || "{}");
  const STORAGE_KEY = "clinic-assistant-history";
  const CONVERSATION_KEY = "clinic-assistant-conversation";
  const SESSION_KEY = "clinic-assistant-session-key";
  const OPEN_STATE_KEY = "clinic-assistant-open";
  const MINIMIZED_STATE_KEY = "clinic-assistant-minimized";

  const panel = root.querySelector(".clinic-assistant__panel");
  const launcher = root.querySelector(".clinic-assistant__launcher");
  const toggle = root.querySelector(".clinic-assistant__toggle");
  const minimize = root.querySelector(".clinic-assistant__minimize");
  const compact = root.querySelector(".clinic-assistant__compact");
  const clear = root.querySelector(".clinic-assistant__clear");
  const form = root.querySelector(".clinic-assistant__form");
  const input = root.querySelector(".clinic-assistant__input");
  const messages = root.querySelector(".clinic-assistant__messages");
  const loading = root.querySelector(".clinic-assistant__loading");
  const prompts = root.querySelectorAll(".clinic-assistant__prompt");
  const endpoint = root.dataset.chatEndpoint;
  const csrfToken = root.dataset.csrfToken;
  const currentSessionKey = root.dataset.sessionKey || "";

  function conversationId() {
    const storedConversationId = window.sessionStorage.getItem(CONVERSATION_KEY);

    // Older browser sessions may still have the earlier conversation token format.
    if (storedConversationId && !storedConversationId.startsWith("resp_")) {
      window.sessionStorage.removeItem(CONVERSATION_KEY);
      return null;
    }

    return storedConversationId;
  }

  function storeConversationId(value) {
    if (!value) return;

    window.sessionStorage.setItem(CONVERSATION_KEY, value);
  }

  function storedSessionKey() {
    return window.sessionStorage.getItem(SESSION_KEY) || "";
  }

  function storeSessionKey(value) {
    if (!value) return;

    window.sessionStorage.setItem(SESSION_KEY, value);
  }

  function resetConversation() {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(CONVERSATION_KEY);
  }

  // Keep the assistant scoped to the current browser session so a
  // logout/login or server-side session reset starts a fresh conversation.
  function syncSessionScope() {
    if (!currentSessionKey) return;

    if (storedSessionKey() && storedSessionKey() !== currentSessionKey) {
      resetConversation();
    }

    storeSessionKey(currentSessionKey);
  }

  function readHistory() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "[]");
    } catch (_error) {
      return [];
    }
  }

  function writeHistory(history) {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  }

  function pushHistory(entry) {
    const history = readHistory();
    history.push(entry);
    writeHistory(history);
  }

  function renderHistory() {
    messages.innerHTML = "";

    readHistory().forEach((entry) => {
      appendMessage(entry.kind, entry.text, false);
    });
  }

  function setOpen(open) {
    panel.classList.toggle("hidden", !open);
    root.classList.toggle("clinic-assistant--open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    minimize.setAttribute("aria-label", open ? "Collapse oVRcome Guide" : "Minimize oVRcome Guide");
    minimize.setAttribute("title", open ? "Collapse oVRcome Guide" : "Minimize oVRcome Guide");
    window.sessionStorage.setItem(OPEN_STATE_KEY, open ? "true" : "false");
    if (open) {
      messages.scrollTop = messages.scrollHeight;
      input.focus();
    }
  }

  function setMinimized(minimized) {
    if (minimized) setOpen(false);

    root.classList.toggle("clinic-assistant--minimized", minimized);
    launcher.classList.toggle("hidden", minimized);
    compact.classList.toggle("hidden", !minimized);
    window.sessionStorage.setItem(MINIMIZED_STATE_KEY, minimized ? "true" : "false");
  }

  function appendMessage(kind, text, persist = true) {
    const message = document.createElement("div");
    message.className = `clinic-assistant__message clinic-assistant__message--${kind}`;
    message.textContent = text;

    messages.appendChild(message);
    message.scrollIntoView({block: "start", behavior: "smooth"});

    if (persist) {
      pushHistory({kind, text});
    }
  }

  function setLoading(isLoading) {
    loading.classList.toggle("hidden", !isLoading);
    input.disabled = isLoading;
    form.querySelector("button[type='submit']").disabled = isLoading;
    prompts.forEach((prompt) => {
      prompt.disabled = isLoading;
    });
  }

  async function askQuestion(question) {
    const trimmed = question.trim();
    if (!trimmed) return;

    appendMessage("user", trimmed);
    input.value = "";
    setLoading(true);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          question: trimmed,
          _csrf_token: csrfToken,
          conversation_id: conversationId(),
          ...pageContext
        })
      });

      const rawBody = await response.text();
      let payload = null;

      try {
        payload = JSON.parse(rawBody);
      } catch (_error) {
        payload = null;
      }

      if (!response.ok) {
        appendMessage("assistant", TEMPORARY_UNAVAILABLE_MESSAGE);
        return;
      }

      if (!payload) {
        appendMessage("assistant", TEMPORARY_UNAVAILABLE_MESSAGE);
        return;
      }

      storeConversationId(payload.conversation_id);
      appendMessage("assistant", payload.answer || "No answer returned.");
    } catch (_error) {
      appendMessage("assistant", TEMPORARY_UNAVAILABLE_MESSAGE);
    } finally {
      setLoading(false);
    }
  }

  toggle.addEventListener("click", () => setOpen(panel.classList.contains("hidden")));
  minimize.addEventListener("click", () => {
    if (!panel.classList.contains("hidden")) {
      setOpen(false);
      return;
    }

    setMinimized(true);
  });
  compact.addEventListener("click", () => {
    setMinimized(false);
    setOpen(true);
  });
  clear.addEventListener("click", () => {
    resetConversation();
    messages.innerHTML = "";
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    askQuestion(input.value);
  });

  // Match typical chat behavior: Enter submits, while Shift+Enter keeps a newline.
  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;

    event.preventDefault();
    askQuestion(input.value);
  });

  prompts.forEach((prompt) => {
    prompt.addEventListener("click", () => {
      setOpen(true);
      askQuestion(prompt.dataset.question || "");
    });
  });

  syncSessionScope();
  renderHistory();

  if (window.sessionStorage.getItem(MINIMIZED_STATE_KEY) == "true") {
    setMinimized(true);
  } else if (window.sessionStorage.getItem(OPEN_STATE_KEY) == "true") {
    setOpen(true);
  }
}

document.addEventListener("DOMContentLoaded", bootClinicAssistant);
